// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Donor Retention (v3)
// Path: src/components/dashboard/pollen/PollenRetention.jsx
//
// Dashboard summary: first-time and repeat retention rates,
// computed in Pace mode (each fiscal year truncated to the
// same elapsed window through today's date) so the numbers
// are apples-to-apples comparable to industry benchmarks.
//
// Links to Reports → Retention for the full analysis (which
// has both Pace and Year-over-Year views).
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { RefreshCw, X, Info } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const PAGE_SIZE = 1000;
const FY_START_MONTH = 6; // July (zero-indexed)

const getCurrentFyStart = () => {
  const now = new Date();
  return now.getMonth() >= FY_START_MONTH ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (yr) => `FY${String(yr).slice(2)}–${String(yr + 1).slice(2)}`;

// Same month/day as today applied to the calendar year that contains the
// "today" portion of the given fiscal year. Used to truncate prior FYs
// to the comparable elapsed window for Pace retention.
const cutoffDateForFy = (fy, today) => {
  const calendarYear = today.getMonth() >= FY_START_MONTH ? fy : fy + 1;
  return new Date(calendarYear, today.getMonth(), today.getDate(), 12, 0, 0);
};

export const PollenRetention = ({ onClose }) => {
  const { palette } = useApp();
  const [metrics, setMetrics] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const load = async () => {
      const allGifts = [];
      let offset = 0;
      while (true) {
        const { data, error } = await supabase
          .from("gifts")
          .select("member_id, gift_date")
          .order("gift_date", { ascending: true })
          .range(offset, offset + PAGE_SIZE - 1);

        if (error || !data) break;
        allGifts.push(...data);
        if (data.length < PAGE_SIZE) break;
        offset += PAGE_SIZE;
      }

      const today = new Date();
      const memberFys = {};
      const fyMembersPace = {};

      allGifts.forEach((gift) => {
        if (!gift.gift_date || !gift.member_id) return;
        const d = new Date(gift.gift_date + "T12:00:00");
        const fy = d.getMonth() >= FY_START_MONTH ? d.getFullYear() : d.getFullYear() - 1;

        if (!memberFys[gift.member_id]) memberFys[gift.member_id] = new Set();
        memberFys[gift.member_id].add(fy);

        if (d <= cutoffDateForFy(fy, today)) {
          if (!fyMembersPace[fy]) fyMembersPace[fy] = new Set();
          fyMembersPace[fy].add(gift.member_id);
        }
      });

      const currentFy = getCurrentFyStart();
      const years = [];

      for (let yr = currentFy; yr >= currentFy - 4; yr--) {
        const currentSet = fyMembersPace[yr] || new Set();
        const priorSet = fyMembersPace[yr - 1] || new Set();

        let firstTimePrior = 0, firstTimeRetained = 0;
        let repeatPrior = 0, repeatRetained = 0;

        priorSet.forEach((memberId) => {
          const theirFys = memberFys[memberId];
          let gaveBeforePrior = false;
          theirFys.forEach((fy) => { if (fy < yr - 1) gaveBeforePrior = true; });

          if (gaveBeforePrior) {
            repeatPrior++;
            if (currentSet.has(memberId)) repeatRetained++;
          } else {
            firstTimePrior++;
            if (currentSet.has(memberId)) firstTimeRetained++;
          }
        });

        let newDonors = 0;
        currentSet.forEach((id) => { if (!priorSet.has(id)) newDonors++; });

        const overallRetained = firstTimeRetained + repeatRetained;

        years.push({
          year: yr,
          label: fyLabel(yr),
          donors: currentSet.size,
          priorDonors: priorSet.size,
          overallRate: priorSet.size > 0 ? Math.round((overallRetained / priorSet.size) * 100) : null,
          firstTimeRate: firstTimePrior > 0 ? Math.round((firstTimeRetained / firstTimePrior) * 100) : null,
          repeatRate: repeatPrior > 0 ? Math.round((repeatRetained / repeatPrior) * 100) : null,
          firstTimeRetained,
          firstTimePrior,
          repeatRetained,
          repeatPrior,
          lapsed: priorSet.size - overallRetained,
          newDonors,
        });
      }

      setMetrics(years);
    };
    load();
  }, []);

  if (!metrics) return null;

  const current = metrics[0];
  const todayLabel = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const rateColor = (rate, hi = 50, lo = 30) => {
    if (rate == null) return palette.text3;
    if (rate >= hi) return "#10b981";
    if (rate >= lo) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: "#fff", fontFamily: FONT_FAMILY }}>
            Donor Retention
          </span>
          <span
            style={{
              fontSize: "0.55rem",
              fontWeight: 600,
              padding: "2px 6px",
              borderRadius: 6,
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
            title={`Each fiscal year truncated to ${todayLabel} for an apples-to-apples comparison`}
          >
            Pace · thru {todayLabel}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            onClick={() => setShowInfo((p) => !p)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", opacity: showInfo ? 1 : 0.5, transition: "opacity 0.15s" }}
          >
            <Info size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}>
            <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
          </button>
        </div>
      </div>

      {/* Info box */}
      {showInfo && (
        <div
          style={{
            padding: "10px 16px",
            fontSize: FONT_SIZES.xs,
            lineHeight: 1.5,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            background: `${palette.accent1}06`,
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          These rates compare each fiscal year through <strong>{todayLabel}</strong> — apples-to-apples
          with industry benchmarks like Fundraising Report Card.{" "}
          <strong>First-Time Retention</strong> (~25% benchmark): of donors who first gave by this date last year,
          what share have given by this date this year — the "second gift" conversion.{" "}
          <strong>Repeat Retention</strong> (~60% benchmark): of established donors active by this date last year,
          what share have given by this date this year. See Reports → Retention for the full analysis,
          including Year-over-Year mode for closed-year reporting.
        </div>
      )}

      {/* Three headline rates */}
      <div style={{ display: "flex", padding: "12px 16px 14px", gap: 8 }}>
        <RateBlock
          label="1st-Time"
          rate={current.firstTimeRate}
          detail={`${current.firstTimeRetained} of ${current.firstTimePrior}`}
          color={rateColor(current.firstTimeRate, 30, 15)}
          palette={palette}
        />
        <RateBlock
          label="Repeat"
          rate={current.repeatRate}
          detail={`${current.repeatRetained} of ${current.repeatPrior}`}
          color={rateColor(current.repeatRate, 60, 40)}
          palette={palette}
        />
        <RateBlock
          label="Overall"
          rate={current.overallRate}
          detail={`${current.lapsed} lapsed · ${current.newDonors} new`}
          color={rateColor(current.overallRate)}
          palette={palette}
        />
      </div>
    </div>
  );
};

// ── Rate Block ────────────────────────────────────────────────
const RateBlock = ({ label, rate, detail, color, palette }) => (
  <div style={{ flex: 1, textAlign: "center" }}>
    <div style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color, fontFamily: FONT_FAMILY }}>
      {rate != null ? `${rate}%` : "—"}
    </div>
    <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY }}>
      {label}
    </div>
    <div style={{ fontSize: "0.45rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
      {detail}
    </div>
  </div>
);
